import Vue from 'vue';
import { apolloProvider } from '@/graphql/vueApollo'; // GraphQL connection
import EventBus from '@/utilities/eventBus';
import logger from 'client-logger';
import { FORBIDDEN } from '@/utilities/errorCodes';

// Queries
import GET_PRODUCT_CLASSES from '@/graphql/queries/getProductClasses.gql';
import GET_PRODUCTS_BY_STORE_AND_CLASS from '@/graphql/queries/getProductsByStoreAndClass.gql';

// Error Handling
import User from '@/store/User';
import AppState from '@/store/AppState';

const apollo = apolloProvider.defaultClient;

// An interface for querying product data. All methods with GraphQL queries by default return cached values, but can be forced to re-fetch
const Product = new Vue({
	data() {
		return {
			// All of the product classes that have been pulled
			allProductClasses: [],

			// An object with keys of classes with values of object with array of errors and products
			allProductData: {}
		};
	},
	computed: {
		// An array of strings of product classes ('ashbury', 'beckridge', etc)
		productClasses() {
			if (!this.allProductClasses || !this.allProductClasses.length) {
				const userStoreRole = User.currentUser.storeRoles[0];
				let storeId;

				// Check if our user has a store role
				if (userStoreRole) {
					// Set their storeId
					storeId = userStoreRole.storeId;
				}

				// Check if there is a store
				if (storeId) {
					// Start the fetch for the data!
					apollo
						.query({
							query: GET_PRODUCT_CLASSES,
							variables: {
								storeId
							},
							fetchPolicy: 'network-only'
						})
						.then(response => {
							this.setProductClasses(response.data.getProductClasses);
						})
						.catch(e => {
							// Returns a nicely formatted GraphQL error object.
							const formattedError = AppState.showGeneralError(e);

							if (formattedError) {
								logger[formattedError.code === FORBIDDEN ? 'warn' : 'error'](
									{ error: (formattedError && formattedError.error) || e, storeId },
									'Failed to get productClasses'
								);
							} else {
								logger.error({ error: e, storeId }, 'Failed to get productClasses');
							}

							// Set it to null so we know there was a problem
							this.setProductClasses(null);
						});
				}
			}

			return this.allProductClasses;
		},
		/**
		 * Gets the needed product data each time we have new allProductClasses data
		 */
		productData() {
			// Let's check if we actually have product classes
			if (this.allProductClasses) {
				const userStoreRole = User.currentUser.storeRoles[0];
				let storeId;

				// Check if our user has a store role
				if (userStoreRole) {
					// Set their storeId
					storeId = userStoreRole.storeId;
				}

				// Check if there is a store
				if (storeId) {
					const classNames = this.productClasses.map(productClass => productClass.name);
					const dataClassNames = Object.keys(this.allProductData);

					// Find the classNames that currently aren't in the productData
					const classesNeeded = classNames.filter(className => !dataClassNames.includes(className));

					// Loop through each product class
					for (const productClass of classesNeeded) {
						// Set the productClass name in the allProductData so we don't make multiple calls for the same class
						this.$set(this.allProductData, productClass, {});

						// For each product class, get the skus!
						apollo
							.query({
								query: GET_PRODUCTS_BY_STORE_AND_CLASS,
								variables: {
									storeId,
									productClass
								},
								fetchPolicy: 'network-only'
							})
							.then(response => {
								const classData = response.data.getProductsByStoreAndClass;

								// Add the response to the productData
								this.$set(this.allProductData, productClass, classData);
							})
							.catch(e => {
								// Returns a nicely formatted GraphQL error object.
								const formattedError = AppState.showGeneralError(e);

								if (formattedError) {
									logger[formattedError.code === FORBIDDEN ? 'warn' : 'error'](
										{ error: (formattedError && formattedError.error) || e, storeId, productClass },
										'Failed to get products by store and class'
									);
								} else {
									logger.error({ error: e, storeId, productClass }, 'Failed to get products by store and class');
								}
							});
					}
				}
			}

			return this.allProductData;
		},
		// Will have all of the products in a single array [sku:, sku: , sku: ]
		productsArray() {
			const productData = this.productData;
			const productsArray = [];

			// Loop through all of the class names
			for (const className of Object.keys(productData)) {
				if (productData[className].products) {
					// Add the products to the array
					productsArray.push(...productData[className].products);
				}
			}

			return productsArray;
		},
		// Will have all the products in an object where the key is the sku
		productsObject() {
			const productData = this.productData;
			const productsObject = {};

			// Loop through all of the class names
			for (const className of Object.keys(productData)) {
				if (productData[className].products) {
					for (const product of productData[className].products) {
						// Add the sku to the object
						productsObject[product.sku] = product;

						// Also add the class to the sku in the object
						productsObject[product.sku].class = className;
					}
				}
			}

			return productsObject;
		}
	},
	methods: {
		/**
		 * Just takes the product classes that are passed in and sets allProductClasses
		 * @param {Array} productClasses - An array of the product classes
		 */
		setProductClasses(productClasses) {
			this.$set(this, 'allProductClasses', productClasses);
		},

		/**
		 * Takes a sku passed in and updates it in our productData
		 * @param {Object} skuDetail - the detail for the sku we want to update
		 */
		updateSku(skuDetail) {
			// Loop through each of the product classes
			for (const productClass of Object.keys(this.allProductData)) {
				const products = this.allProductData[productClass].products;

				// Loop through each of the products
				for (const [i, product] of products.entries()) {
					// Check if the skus match
					if (product.sku === skuDetail.sku) {
						// Update the product data with the new sku data
						this.$set(this.allProductData[productClass].products, i, skuDetail);

						return;
					}
				}
			}
		},
		// A method that will accept incoming subscription payloads and do with them what needs to be done
		subscriptionHandler(payload) {
			// We could have very different actions to execute depending on the scenario
			switch (payload.action) {
				case 'updateSku':
					this.updateSku(payload.skuDetail);
					break;
			}
		},
		// Wipes everything out that we have available directly through our props
		clearAll() {
			this.$set(this, 'allProductClasses', []);
			this.$set(this, 'allProductData', {});
		}
	},
	created() {
		// Now to hook into real-time data updates
		EventBus.$on('product', this.subscriptionHandler);
	}
});

export default Product;
